import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledSelect from '../styled/StyledSelect';
import { COUNTRIES, CURRENCIES } from '../../../../shared/CountriesData';
import StyledDropZone from '../styled/StyledDropZone';
import TarifList from '../tarifs/TarifList';
import { Label } from '../ui/label';
import { BENEFIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import { Control, useFormContext } from 'react-hook-form';
import { ActivityFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslation } from 'react-i18next';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { ActivityResponse } from '@/types/benefits/ActivityResponse';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { CitiesResponse } from '@/api/benefit.api';
import { useState } from 'react';
import { BenefitType } from '@/types/benefits/benefitTypes';

interface ActivityConfigurationProps {
  control: Control<ActivityFormDataWithTranslations>;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  cities?: CitiesResponse;
  language: SupportedLocale;
  isEditEnabled: boolean;
  translateFields: (
    fieldsToTranslate?: Array<
      'benefit.name' | 'shortDescription' | 'longDescription'
    >,
  ) => Promise<void>;
  isTranslating: boolean;
  errorsLength: number;
  activityType: ActivityResponse['activityType'];
}

const ActivityConfiguration = ({
  control,
  suppliers,
  isEditEnabled,
  language,
  translateFields,
  isTranslating,
  errorsLength,
  activityType,
  cities,
}: ActivityConfigurationProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { setValue } = useFormContext<ActivityFormDataWithTranslations>();

  const changeSupplierWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'supplier',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const supplier = suppliers?.find(
        (supplier) => supplier.id === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.value === supplier?.currency,
      );
      return currency;
    },
  });

  const suppliersOptions =
    suppliers?.map(({ id, name }) => ({ value: id, label: name })) ?? [];

  // Use derived state for city options from API, plus state for custom options
  const apiCityOptions = cities
    ? cities.map(({ city }: { city: string }) => ({
        value: city,
        label: city,
      }))
    : [];
  const [customCityOptions, setCustomCityOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const cityOptions = [...apiCityOptions, ...customCityOptions];

  const getNameTitle = () => {
    switch (activityType) {
      case BenefitType.ACTIVITY:
        return t('activities.activityName');
      case BenefitType.MEAL:
        return t('activities.mealName');
      case BenefitType.TRANSFER:
        return t('activities.transferName');
      default:
        return t('activities.activityName');
    }
  };

  const getNamePlaceholder = () => {
    switch (activityType) {
      case BenefitType.ACTIVITY:
        return t('activities.activityNamePlaceholder');
      case BenefitType.MEAL:
        return t('activities.mealNamePlaceholder');
      case BenefitType.TRANSFER:
        return t('activities.transferNamePlaceholder');
      default:
        return t('activities.activityNamePlaceholder');
    }
  };

  const getDescriptionPlaceholder = () => {
    switch (activityType) {
      case BenefitType.ACTIVITY:
        return t('activities.shortDescriptionPlaceholder');
      case BenefitType.MEAL:
        return t('activities.mealNamePlaceholder');
      case BenefitType.TRANSFER:
        return t('activities.transferNamePlaceholder');
      default:
        return t('activities.shortDescriptionPlaceholder');
    }
  };

  return (
    <>
      <StyledFormSection
        sectionTitle={t('forms.sections.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['benefit.name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        {errorsLength > 0 && (
          <Label className="text-error-500">
            {t('common.checkRequiredFields')}
          </Label>
        )}
        <StyledTranslatableInput
          control={control}
          name="benefit.name"
          originalName="name"
          setValue={setValue}
          language={language}
          title={getNameTitle()}
          placeholder={getNamePlaceholder()}
          disabled={!isEditEnabled}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={suppliersOptions}
            control={control}
            name="supplier"
            title={t('forms.labels.supplier') + ' *'}
            placeholder={t('forms.placeholders.exampleName')}
            isClearable
            disabled={!isEditEnabled}
            onChange={changeSupplierWithCurrency}
          />
          <StyledSelect
            options={CURRENCIES}
            control={control}
            name="currency"
            title={t('forms.labels.currency') + ' *'}
            placeholder={t('forms.placeholders.selectCurrency')}
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={COUNTRIES}
            control={control}
            name="country"
            title={t('forms.labels.country') + ' *'}
            placeholder={t('forms.placeholders.selectCountry')}
            disabled={!isEditEnabled}
          />
          <StyledSelect
            options={cityOptions}
            control={control}
            name="city"
            title={t('forms.labels.city')}
            placeholder={t('forms.placeholders.selectCity')}
            creatable
            onCreateOption={(option) =>
              setCustomCityOptions((prev) => [
                ...prev,
                { label: option, value: option },
              ])
            }
            disabled={!isEditEnabled}
            isClearable
          />
        </div>
        <MarginRateStyledInput
          control={control}
          name="marginRate"
          title={getTranslatedConst(BENEFIT_MARGIN_RATE_LABEL)}
          placeholder={t('forms.placeholders.addRate')}
          disabled={!isEditEnabled}
        />
        {isEditEnabled && (
          <Label className="text-gray-700">
            {t('common.requiredFieldsNote')}
          </Label>
        )}
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('forms.sections.description')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields(['shortDescription', 'longDescription'])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <StyledTranslatableTextarea
          control={control}
          name="shortDescription"
          originalName="shortDescription"
          setValue={setValue}
          language={language}
          title={t('activities.shortDescription')}
          placeholder={getDescriptionPlaceholder()}
          disabled={!isEditEnabled}
          rows={1}
        />
        <StyledTranslatableTextarea
          control={control}
          name="longDescription"
          originalName="longDescription"
          setValue={setValue}
          language={language}
          title={t('activities.longDescription')}
          placeholder={getDescriptionPlaceholder()}
          rows={3}
          disabled={!isEditEnabled}
        />
        <StyledDropZone
          control={control}
          name="images"
          title={t('forms.labels.gallery')}
          placeholder="PNG, JPG"
          multiple
          disabled={!isEditEnabled}
        />
      </StyledFormSection>
      <HorizontalLine />
      <TarifList control={control} isViewMode={!isEditEnabled} />
    </>
  );
};

export default ActivityConfiguration;
